import React, {useContext, useState} from 'react';
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {useMiniGameData} from "../../../logs/useTableData";
import {Descriptions, Table, Tabs, Tag, Card, Typography, Tooltip} from "antd";

import dayjs from "dayjs";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import {useKrTextData} from "../../../probability/useItemData";
import {AuthContext} from "../../../../shared/contexts/authContext";
const {Title} = Typography;

const CharacterPackage = () => {
    const {currentUser} = useContext(AuthContext);
    const {playerData} = useContext(PlayerContext);

    const [currentMissionData] = useState(playerData.playerInfo.shopData?.ActiveMemoryGameInfo || {});
    const [activeCharacterPackageKey] = useState(currentMissionData?.packageId || '');
    const [characterPackage] = useState(playerData.playerInfo.shopData?.DicPackageDataCharacter?.NeedUserLevel[activeCharacterPackageKey] || {});
    const [miniGameIndex] = useState(currentMissionData?.packageGroupId || 0);
    const [miniGame] = useState(characterPackage?.purchasedGroupInfo?.find(game => game.Index === miniGameIndex) || {});

    const {data: miniGameData, isLoading: isMiniGameLoading, error: miniGameError} = useMiniGameData();
    const {data: krTextData, isLoading: isTextLoading, error: textError} = useKrTextData();

    const stateText = {
        0: { text: '닫힘', color: 'default' },
        1: { text: '열림', color: 'blue' },
        2: { text: '완료', color: 'green' },
        3: { text: '보상받음', color: 'gold' },
    };

    const columns = [
        {
            title: '미션이름',
            dataIndex: 'id',
            key: 'id',
            width: 200,
            render: (id) => (
                <span>{krTextData[currentUser.target][miniGameData.miniGameMission[id].missionLIdx]?.text || 'Unknown Mission'}</span>
            )
        },
        {
            title: 'count',
            dataIndex: 'count',
            key: 'count',
            width: 100,
            render: (count) => <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{count}</span>,
        },
        {
            title: '상태',
            dataIndex: 'state',
            key: 'state',
            width: 120,
            render: (state) => {
                const info = stateText[state] || { text: state, color: 'default' };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
    ];

    if (isMiniGameLoading || isTextLoading) return <IsLoading />;
    if (miniGameError || textError) return <ErrorPage error={miniGameError} />;
    if (characterPackage === {} || miniGame === {}) {
        return <ErrorPage error={new Error('Character Package or Mini Game data not found.')} />;
    }

    return (
        <div>
            <Descriptions title="미니게임 정보" bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="캐릭터 패키지 인덱스">{characterPackage?.Index}</Descriptions.Item>
                <Descriptions.Item label="캐릭퍼 패키지 시퀀스">{characterPackage?.seqForDate}</Descriptions.Item>
                <Descriptions.Item label="선택 캐릭터">{miniGameIndex}</Descriptions.Item>
                <Descriptions.Item label="상태">{stateText[miniGame?.InfoState]?.text}</Descriptions.Item>
                <Descriptions.Item label="미니게임 시작 일자">{dayjs(miniGame?.PurchasesDate).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
            </Descriptions>
            <Card style={{ marginTop: 24 }}>
                <Title level={4} style={{ marginBottom: 8 }}>메모리 카드</Title>
                <div style={{ marginBottom: 16, display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>범례:</span>
                    <div style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '2px solid #52c41a',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                    }} />
                    <span style={{ fontSize: '14px' }}>활성화 (State: 2)</span>
                    <div style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '2px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        opacity: 0.5
                    }} />
                    <span style={{ fontSize: '14px' }}>비활성화 (State: 1)</span>
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridTemplateRows: 'repeat(4, 1fr)',
                            gap: '8px',
                            width: '100%',
                            maxWidth: '400px',
                            padding: '16px',
                            backgroundColor: '#f0f2f5',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                    >
                        {currentMissionData.memoryCards && currentMissionData.memoryCards.length > 0 ? (
                            currentMissionData.memoryCards.map((card, index) => {
                                if (!card) return null;
                                const cardInfo = miniGameData.miniGameCardInfo[card.imageIndex];
                                const isActive = card.state === 2;
                                const imageUrl = cardInfo?.imgPath ? (
                                    cardInfo.imgPath.startsWith('/') ? cardInfo.imgPath : `/${cardInfo.imgPath}`
                                ) : null;

                                return (
                                    <Tooltip
                                        key={card.id + '-' + index}
                                        title={`ID: ${card.id} | State: ${isActive ? '활성화' : '비활성화'}`}
                                    >
                                        <div
                                            style={{
                                                aspectRatio: '1 / 1',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                border: `2px solid ${isActive ? '#52c41a' : '#d9d9d9'}`,
                                                backgroundColor: '#fff',
                                                opacity: isActive ? 1 : 0.5,
                                                transition: 'all 0.3s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={`Card ${card.id}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '12px' }}>
                                                    No Image
                                                </span>
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            })
                        ) : (
                            Array.from({ length: 12 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        aspectRatio: '1 / 1',
                                        borderRadius: '8px',
                                        backgroundColor: '#e8e8e8',
                                        border: '1px solid #d9d9d9',
                                    }}
                                />
                            ))
                        )}
                    </div>
                </div>
            </Card>
            <Table
                columns={columns}
                dataSource={Object.values(currentMissionData?.missionData)}
                pagination={false}
                bordered
                size="small"
            />
        </div>
    );
};

export default CharacterPackage;